import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./PricingScreen.module.css";
import { FaCheckCircle } from "react-icons/fa"; /* Đã xóa FaPaypal */
import { getDatabase, ref, get, set, push } from "firebase/database";
import { trackButtonClick } from "../utils/analytics";

import {
  trackEvent,
} from "../utils/metaPixel";
import { createPayOSPayment, checkPaymentServerStatus } from "../api/payment";

const PricingScreen = () => {
  const [currentMembership, setCurrentMembership] = useState(null);
  const [membershipExpiration, setMembershipExpiration] = useState(null);
  const [message, setMessage] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false); // Thêm state cho modal video
  const [isTestingPayment, setIsTestingPayment] = useState(false); // State cho test payment
  const [isTestUser, setIsTestUser] = useState(false); // State cho test user
  const navigate = useNavigate();
  const location = useLocation();

  // Không cần kiểm tra ngôn ngữ hiện tại từ localStorage nữa
  useEffect(() => {
    // Đã loại bỏ code không cần thiết
  }, []);

  // Xác định ký hiệu tiền tệ dựa vào ngôn ngữ
  const getCurrencySymbol = () => {
    return "₫";
  };

  const checkLoginStatus = () => {
    const accessToken = localStorage.getItem("userAccessToken");
    return !!accessToken;
  };

  const plans = [
    {
      id: 0,
      name: "Gói Demo",
      price: "Miễn phí",
      originalPrice: null,
      discount: 0,
      quota: 0, // Demo - không có quota
      quotaDescription: "Xem demo",
      features: [
        "Xem trọn bộ video demo",
        "Hiểu quy trình định giá AI",
        "Khám phá giao diện sản phẩm",
        "Tìm hiểu tính năng nổi bật",
        "Hướng dẫn sử dụng chi tiết",
      ],
      recommended: false,
      duration: "Xem demo",
      period: "Xem demo",
      isDemo: true,
    },
    {
      id: 1,
      name: "Gói Cá Nhân",
      price: "29.000",
      originalPrice: null,
      discount: 0,
      quota: 1, // 1 lượt định giá
      quotaDescription: "1 lượt định giá",
      features: [
        "1 lượt định giá",
        "Đa dạng bất động sản",
        "Chỉ cần thông tin cơ bản",
        "Phân tích giá AI chuyên sâu",
        "Mẫu báo cáo kết quả chuyên nghiệp",
      ],
      recommended: false,
      duration: "1 lượt",
      period: "1 lượt",
    },
    {
      id: 2,
      name: "Gói Cơ Bản",
      price: "239.000",
      originalPrice: null,
      discount: 0,
      quota: 10, // 10 lượt định giá
      quotaDescription: "10 lượt định giá/tháng",
      features: [
        "10 lượt định giá",
        "Đa dạng bất động sản",
        "Chỉ cần thông tin cơ bản",
        "Phân tích giá AI chuyên sâu",
        "Mẫu báo cáo kết quả chuyên nghiệp",
      ],
      recommended: true,
      duration: 30,
      period: "Hàng tháng",
    },
    {
      id: 3,
      name: "Gói Chuyên Nghiệp",
      price: "569.000",
      originalPrice: null,
      discount: 0,
      quota: 30, // 30 lượt định giá
      quotaDescription: "30 lượt định giá/tháng",
      features: [
        "30 lượt định giá",
        "Đa dạng bất động sản",
        "Chỉ cần thông tin cơ bản",
        "Phân tích giá AI chuyên sâu",
        "Mẫu báo cáo kết quả chuyên nghiệp",
      ],
      recommended: false,
      duration: 30,
      period: "Hàng tháng",
    },
    {
      id: 4,
      name: "Gói Doanh Nghiệp",
      price: "1.499.000",
      originalPrice: null,
      discount: 0,
      quota: 100, // 100 lượt định giá
      quotaDescription: "100 lượt định giá/tháng",
      features: [
        "100 lượt định giá",
        "Đa dạng bất động sản",
        "Chỉ cần thông tin cơ bản",
        "Phân tích giá AI chuyên sâu",
        "Mẫu báo cáo kết quả chuyên nghiệp",
      ],
      recommended: false,
      duration: 30,
      period: "Hàng tháng",
    },
  ];

  useEffect(() => {
    const fetchMembership = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) {
        const db = getDatabase();
        const membershipRef = ref(db, `/users/${userId}/membership`);
        const expirationRef = ref(db, `/users/${userId}/membershipExpiry`);
        const testUserRef = ref(db, `/users/${userId}/isTestUser`);
        try {
          const membershipSnapshot = await get(membershipRef);
          if (membershipSnapshot.exists()) {
            const membershipValue = membershipSnapshot.val();
            console.log("Membership data from database:", {
              rawValue: membershipValue,
              type: typeof membershipValue,
            });
            setCurrentMembership(membershipValue);
          }

          const expirationSnapshot = await get(expirationRef);
          if (expirationSnapshot.exists()) {
            setMembershipExpiration(expirationSnapshot.val());
          }

          const testUserSnapshot = await get(testUserRef);
          if (testUserSnapshot.exists()) {
            setIsTestUser(testUserSnapshot.val());
          }
        } catch (error) {
          console.error("Lỗi khi lấy thông tin membership:", error);
        }
      }
    };

    fetchMembership();
  }, []);

  // Check payment server status on component mount
  useEffect(() => {
    const checkServerStatus = async () => {
      try {
        const isServerRunning = await checkPaymentServerStatus();
        if (!isServerRunning) {
          console.warn(
            "⚠️ Payment server is not running. Please start the payment server."
          );
        } else {
          console.log("✅ Payment server is running and ready");
        }
      } catch (error) {
        console.warn(
          "⚠️ Could not check payment server status:",
          error.message
        );
      }
    };

    checkServerStatus();
  }, []);

  useEffect(() => {
    // Kiểm tra trạng thái đơn hàng
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      setMessage(
        "Thanh toán thành công. Cảm ơn bạn đã sử dụng dịch vụ định giá AI của chúng tôi"
      );

      // Xóa query params sau một khoảng thời gian
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 5000); // Xóa sau 5 giây
    }

    if (query.get("canceled")) {
      setMessage(
        "Thanh toán thất bại. Nếu có bất kỳ câu hỏi nào hãy gửi email tới contact@youhomes.vn"
      );

      // Xóa query params sau một khoảng thời gian
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 5000); // Xóa sau 5 giây
    }
  }, []);

  const getMembershipStatus = (planLevel) => {
    if (!currentMembership) return null;

    // Chuyển đổi membership sang số để đảm bảo so sánh chính xác
    const membershipId =
      typeof currentMembership === "string"
        ? parseInt(currentMembership)
        : currentMembership;

    // So sánh planLevel với membershipId đã chuẩn hóa
    if (planLevel === membershipId) {
      return "Gói hiện tại của bạn";
    }

    return null;
  };

  // Kiểm tra xem gói có thấp hơn gói hiện tại không
  const isLowerThanCurrentPlan = (planLevel) => {
    if (!currentMembership) return false;

    // Chuyển đổi membership sang số để đảm bảo so sánh chính xác
    const membershipId =
      typeof currentMembership === "string"
        ? parseInt(currentMembership)
        : currentMembership;

    return planLevel < membershipId;
  };

  const formatExpirationDate = (timestamp) => {
    if (!timestamp) return "";

    // Xử lý nhiều định dạng thời gian có thể có
    let date;
    if (typeof timestamp === "number") {
      // Timestamp dạng số (Unix timestamp)
      // Kiểm tra nếu timestamp quá lớn (dạng milliseconds)
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        // Nếu là seconds, chuyển thành milliseconds
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      // Timestamp dạng string (ISO string hoặc tùy chỉnh)
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      // Timestamp từ Firestore
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "Không xác định";
    }

    // Kiểm tra xem date có hợp lệ không
    if (isNaN(date.getTime())) {
      return "Không xác định";
    }

    // Định dạng ngày/tháng/năm và giờ
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} (${hours}:${minutes})`;
  };

  const getRemainingTime = (timestamp) => {
    if (!timestamp) return "";

    // Xử lý timestamp tương tự như formatExpirationDate
    let date;
    if (typeof timestamp === "number") {
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "";
    }

    // Kiểm tra ngày hợp lệ
    if (isNaN(date.getTime())) {
      return "";
    }

    const now = new Date();
    const diff = date.getTime() - now.getTime();

    // Nếu đã hết hạn
    if (diff < 0) {
      return "Đã hết hạn";
    }

    // Tính toán số ngày còn lại
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 30) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      return `Còn ${months} tháng ${remainingDays} ngày`;
    } else {
      return `Còn ${days} ngày`;
    }
  };

  const handleSubscribe = (plan) => {
    trackButtonClick("subscribe_plan", {
      plan_name: plan.name,
      plan_price: plan.price,
    });

    // Xử lý gói Demo - mở video demo
    if (plan.isDemo) {
      handleWatchDemo();
      return;
    }

    if (!checkLoginStatus()) {
      navigate("/login");
      return;
    }

    // Luôn sử dụng PayOS
    createPayOSPayment(plan);
  };

  const handleWatchDemo = () => {
    setIsVideoModalOpen(true);
    // Track demo video view
    trackEvent("DemoVideoView", { button_location: "pricing_screen" });
    trackButtonClick("watch_demo_button", { location: "pricing_screen" });
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  // Kiểm tra kết quả thanh toán khi quay lại từ PayOS
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const status = queryParams.get("status");
    const paymentId = queryParams.get("paymentId");

    // Chỉ xử lý khi có status và paymentId, không xử lý cho success/canceled mới
    if (
      status &&
      paymentId &&
      !queryParams.get("success") &&
      !queryParams.get("canceled")
    ) {
      // Xác minh thanh toán với backend
      const verifyPayment = async () => {
        try {
          const response = await fetch(
            `/api/verify-payment?paymentId=${paymentId}`
          );
          const result = await response.json();

          if (result.success) {
            // Xóa dữ liệu thanh toán khỏi localStorage sau khi đã xử lý
            localStorage.removeItem("latestPaymentData");

            alert(
              "Thanh toán thành công! Gói định giá AI của bạn sẽ được kích hoạt sau vài phút."
            );
          } else {
            alert(
              "Xác minh thanh toán không thành công, vui lòng liên hệ hỗ trợ."
            );
          }

          // Xóa query params
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        } catch (error) {
          console.error("Lỗi khi xác minh thanh toán:", error);
        }
      };

      verifyPayment();
    }
  }, [location.search]);

  const handleBack = () => {
    // Nếu đến từ menu (có state.fromMenu), quay lại trang trước đó
    if (location.state?.fromMenu) {
      navigate(-1);
    } else {
      // Nếu vào trực tiếp từ URL, chuyển về landing page
      navigate("/landing-page");
    }
  };

  const handleContactClick = () => {
    trackButtonClick("contact_from_pricing");
    window.open("/landing-page#contact", "_blank");
  };

  // Test payment function cho môi trường dev
  const handleTestPayment = async (plan) => {
    if (process.env.NODE_ENV !== "development") {
      console.warn("Test payment chỉ khả dụng trong môi trường development");
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("Vui lòng đăng nhập để test payment");
      return;
    }

    setIsTestingPayment(true);

    try {
      const db = getDatabase();

      // Tính toán thời gian hết hạn dựa trên plan
      let expirationTime;
      if (plan.id === 1) {
        // Gói Cá Nhân - 1 lượt
        expirationTime = null; // Không có hạn sử dụng cho gói 1 lượt
      } else {
        // Các gói khác: thêm 30 ngày từ hiện tại
        const now = new Date();
        const expiration = new Date(
          now.getTime() + plan.duration * 24 * 60 * 60 * 1000
        );
        expirationTime = expiration.getTime();
      }

      // Lấy quota từ plan
      const planQuota = plan.quota;

      // Cập nhật membership và quota
      const membershipRef = ref(db, `/users/${userId}/membership`);
      await set(membershipRef, plan.id);

      // Cập nhật quota cho user
      if (planQuota > 0) {
        const quotaRef = ref(db, `/users/${userId}/quota`);
        const usedQuotaRef = ref(db, `/users/${userId}/usedQuota`);

        // Set total quota và reset used quota
        await set(quotaRef, planQuota);
        await set(usedQuotaRef, 0); // Reset về 0 khi mua plan mới

        console.log(`✅ [DEV TEST] Quota updated: ${planQuota} lượt định giá`);
      }

      // Cập nhật expiration nếu có
      if (expirationTime) {
        const expirationRef = ref(db, `/users/${userId}/membershipExpiry`);
        await set(expirationRef, expirationTime);
        setMembershipExpiration(expirationTime);
      } else {
        // Xóa expiration cho gói 1 lượt
        const expirationRef = ref(db, `/users/${userId}/membershipExpiry`);
        await set(expirationRef, null);
        setMembershipExpiration(null);
      }

      // Thêm transaction record vào payment-history để quản lý tập trung
      const paymentHistoryRef = ref(db, "payment-history");
      const transactionData = {
        userId: userId,
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        status: "COMPLETED", // PayOS standard: PAID -> COMPLETED
        paymentMethod: "TEST_DEV",
        orderCode: `DEV_TEST_${Date.now()}`, // PayOS requires orderCode
        paymentLinkId: `test_link_${Date.now()}`, // PayOS standard field
        description: `${plan.name} - AIPrice Dev Test`,
        timestamp: Date.now(),
        updatedAt: Date.now(),
        createdAt: Date.now(),
        completedAt: Date.now(), // PayOS standard for completed payments
        expirationTime: expirationTime,
        // Quota information
        planQuota: planQuota,
        quotaDescription: plan.quotaDescription,
        // PayOS transaction details (simulated for dev)
        transactionDateTime: new Date().toISOString(),
        reference: `DEV_REF_${Date.now()}`,
        counterAccountName: "Dev Test Account",
        counterAccountBankName: "Dev Test Bank",
        // User info
        userEmail: localStorage.getItem("userEmail") || "",
        source: "dev_test",
      };
      await push(paymentHistoryRef, transactionData);

      // Cập nhật state local
      setCurrentMembership(plan.id);

      // Hiển thị thông báo thành công
      const quotaMessage = planQuota > 0 ? ` | ${plan.quotaDescription}` : "";
      setMessage(
        `✅ [DEV TEST] Đã cập nhật thành công gói "${plan.name}". Membership ID: ${plan.id}${quotaMessage}`
      );

      // Track test event
      trackEvent("TestPaymentCompleted", {
        plan_id: plan.id,
        plan_name: plan.name,
        environment: "development",
      });

      console.log("🧪 [DEV TEST] Payment simulation completed:", {
        planId: plan.id,
        planName: plan.name,
        planQuota: planQuota,
        quotaDescription: plan.quotaDescription,
        expirationTime: expirationTime
          ? new Date(expirationTime).toISOString()
          : "No expiration",
        userId: userId,
      });
    } catch (error) {
      console.error("❌ [DEV TEST] Error simulating payment:", error);
      setMessage(`❌ [DEV TEST] Lỗi khi test payment: ${error.message}`);
    } finally {
      setIsTestingPayment(false);
    }
  };

  // Reset membership function cho môi trường dev
  // eslint-disable-next-line no-unused-vars
  const handleResetMembership = async () => {
    if (process.env.NODE_ENV !== "development") {
      console.warn(
        "Reset membership chỉ khả dụng trong môi trường development"
      );
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("Vui lòng đăng nhập để reset membership");
      return;
    }

    if (
      !window.confirm(
        "Bạn có chắc chắn muốn reset membership về null? (Dev test only)"
      )
    ) {
      return;
    }

    setIsTestingPayment(true);

    try {
      const db = getDatabase();

      // Reset membership về null
      const membershipRef = ref(db, `/users/${userId}/membership`);
      await set(membershipRef, null);

      // Reset expiration về null
      const expirationRef = ref(db, `/users/${userId}/membershipExpiry`);
      await set(expirationRef, null);

      // Reset quota và usedQuota về null
      const quotaRef = ref(db, `/users/${userId}/quota`);
      const usedQuotaRef = ref(db, `/users/${userId}/usedQuota`);
      await set(quotaRef, null);
      await set(usedQuotaRef, null);

      // Cập nhật state local
      setCurrentMembership(null);
      setMembershipExpiration(null);

      // Hiển thị thông báo
      setMessage(
        `🔄 [DEV TEST] Đã reset membership về null. Có thể test lại các plan khác.`
      );

      console.log("🔄 [DEV TEST] Membership reset completed");
    } catch (error) {
      console.error("❌ [DEV TEST] Error resetting membership:", error);
      setMessage(`❌ [DEV TEST] Lỗi khi reset membership: ${error.message}`);
    } finally {
      setIsTestingPayment(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <button onClick={handleBack} className={styles.backButton}>
          ← Quay lại
        </button>

        {message && (
          <div
            className={`${styles.messageAlert} ${
              typeof message === "string" && message.includes("thành công")
                ? styles.successAlert
                : styles.errorAlert
            }`}
          >
            {message}
          </div>
        )}

        {/* NEW: Free Model Banner */}
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "48px 24px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "24px",
              padding: "48px",
              color: "white",
              boxShadow: "0 20px 60px rgba(102, 126, 234, 0.3)",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>🎉</div>
            <h1
              style={{
                fontSize: "42px",
                fontWeight: "bold",
                marginBottom: "16px",
                lineHeight: "1.2",
              }}
            >
              Sử dụng miễn phí
            </h1>
            <p
              style={{
                fontSize: "24px",
                marginBottom: "32px",
                opacity: 0.9,
              }}
            >
              3 lượt định giá mỗi ngày - Hoàn toàn miễn phí
            </p>

            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "16px",
                padding: "24px",
                marginBottom: "32px",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                ✨ Tính năng đầy đủ
              </h3>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  fontSize: "16px",
                  lineHeight: "2",
                }}
              >
                <li>✓ Định giá BDS chính xác với AI</li>
                <li>✓ Định giá thuê BDS</li>
                <li>✓ Lịch sử định giá đầy đủ</li>
                <li>✓ Báo cáo chi tiết PDF</li>
                <li>✓ Hỗ trợ 24/7</li>
              </ul>
            </div>

            <button
              onClick={() => {
                if (checkLoginStatus()) {
                  navigate("/ai-price");
                } else {
                  navigate("/");
                }
              }}
              style={{
                backgroundColor: "white",
                color: "#667eea",
                border: "none",
                borderRadius: "12px",
                padding: "16px 48px",
                fontSize: "18px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s",
                boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)";
              }}
            >
              {checkLoginStatus()
                ? "Bắt đầu định giá ngay"
                : "Đăng ký miễn phí"}
            </button>

            <p
              style={{
                marginTop: "24px",
                fontSize: "14px",
                opacity: 0.8,
              }}
            >
              📊 Dữ liệu của bạn giúp chúng tôi cải thiện dịch vụ
            </p>
          </div>

          <div
            style={{
              marginTop: "48px",
              padding: "24px",
              backgroundColor: "#f9fafb",
              borderRadius: "16px",
              textAlign: "left",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#333",
              }}
            >
              ❓ Câu hỏi thường gặp
            </h3>
            <div style={{ color: "#666", lineHeight: "1.8" }}>
              <p style={{ marginBottom: "12px" }}>
                <strong>Q: Có giới hạn gì không?</strong>
                <br />
                A: Mỗi ngày bạn có 3 lượt định giá miễn phí. Số lượt sẽ được
                reset vào 00:00 mỗi ngày.
              </p>
              <p style={{ marginBottom: "12px" }}>
                <strong>Q: Tôi cần thanh toán không?</strong>
                <br />
                A: Không! Tất cả tính năng hoàn toàn miễn phí.
              </p>
              <p>
                <strong>Q: Dữ liệu của tôi có an toàn không?</strong>
                <br />
                A: Chúng tôi cam kết bảo mật thông tin và chỉ sử dụng dữ liệu để
                cải thiện dịch vụ.
              </p>
            </div>
          </div>
        </div>

        {/* OLD: Pricing Plans - HIDDEN */}
        <div className={styles.plansContainer} style={{ display: "none" }}>
          {plans.map((plan, index) => {
            // Debug values for each plan
            const planLevel = plan.id; // Sử dụng plan.id thay vì index + 1
            const isCurrentPlan = getMembershipStatus(planLevel) !== null;
            const isLowerPlan = isLowerThanCurrentPlan(planLevel);

            // Only log for development
            if (process.env.NODE_ENV !== "production") {
              console.log(`Plan ${plan.name} (level ${planLevel}):`, {
                currentMembership,
                isCurrentPlan,
                isLowerPlan,
                buttonState: isCurrentPlan
                  ? "Current"
                  : isLowerPlan
                  ? "Lower"
                  : "Available",
              });
            }

            return (
              <div
                key={plan.name}
                className={`${styles.planCard} ${
                  plan.recommended ? styles.recommended : ""
                }`}
              >
                {plan.recommended && (
                  <div className={styles.recommendedBadge}>Phổ biến nhất</div>
                )}
                <h2 className={styles.planName}>
                  {plan.name}
                  {plan.discount > 0 && (
                    <span className={styles.discountBadge}>
                      -{plan.discount}%
                    </span>
                  )}
                </h2>
                <div className={styles.quotaInfo}>
                  <span className={styles.quota}>{plan.quotaDescription}</span>
                </div>
                <div className={styles.price}>
                  {plan.isCustom ? (
                    <span className={styles.amount}>{plan.price}</span>
                  ) : (
                    <>
                      <span className={styles.currency}>
                        {getCurrencySymbol()}
                      </span>
                      <span className={styles.amount}>{plan.price}</span>
                      {plan.originalPrice && plan.discount > 0 && (
                        <div className={styles.originalPrice}>
                          <span className={styles.currency}>
                            {getCurrencySymbol()}
                          </span>
                          {plan.originalPrice}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <ul className={styles.features}>
                  {plan.features.map((feature, index) => (
                    <li
                      key={index}
                      className={`${styles.feature} ${
                        feature.highlight ? styles.highlightFeature : ""
                      }`}
                    >
                      {/* Use FaCheckCircle icon */}
                      <span className={styles.checkmark}>
                        <FaCheckCircle />
                      </span>
                      <span>
                        {typeof feature === "string" ? feature : feature.text}
                      </span>{" "}
                      {/* Wrap feature text in span for potential styling */}
                    </li>
                  ))}
                </ul>
                <button
                  className={`${styles.button} ${
                    plan.recommended ? styles.recommendedButton : ""
                  }`}
                  onClick={() => {
                    if (plan.isCustom) {
                      handleContactClick();
                    } else {
                      handleSubscribe(plan);
                    }
                  }}
                  disabled={
                    !plan.isDemo &&
                    (getMembershipStatus(planLevel) !== null ||
                      isLowerThanCurrentPlan(planLevel))
                  }
                >
                  {plan.isDemo
                    ? "Xem demo"
                    : plan.isCustom
                    ? "Liên hệ ngay"
                    : getMembershipStatus(planLevel)
                    ? "Gói hiện tại"
                    : isLowerThanCurrentPlan(planLevel)
                    ? "Đã dùng gói cao hơn"
                    : "Đăng ký ngay"}
                </button>

                {/* Test button chỉ hiển thị trong môi trường dev */}
                {process.env.NODE_ENV === "development" &&
                  !plan.isDemo &&
                  !plan.isCustom && (
                    <button
                      className={`${styles.button} mt-2 bg-purple-600 hover:bg-purple-700 text-white font-medium text-sm`}
                      onClick={() => handleTestPayment(plan)}
                      disabled={isTestingPayment}
                      style={{
                        backgroundColor: "#7c3aed",
                        marginTop: "8px",
                        fontSize: "14px",
                        padding: "8px 16px",
                      }}
                    >
                      {isTestingPayment
                        ? "🧪 Testing..."
                        : "🧪 Test Payment (Dev)"}
                    </button>
                  )}
                {getMembershipStatus(planLevel) && membershipExpiration && (
                  <div className={styles.expirationInfo}>
                    <div>
                      Hết hạn: {formatExpirationDate(membershipExpiration)}
                    </div>
                    <div className={styles.remainingTime}>
                      {getRemainingTime(membershipExpiration)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Video Modal */}
        {isVideoModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto relative">
              {/* Close Button */}
              <button
                onClick={closeVideoModal}
                className="absolute top-4 right-4 bg-gray-100 hover:bg-gray-200 rounded-full w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all duration-200 z-10"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Video Title */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  🎥 Demo AIPrice - Hướng dẫn định giá BĐS thông minh
                </h3>
                <p className="text-gray-600">
                  Xem chi tiết cách sử dụng AIPrice để định giá bất động sản
                  nhanh chóng và chính xác
                </p>
              </div>

              {/* YouTube Video Embed */}
              <div
                className="relative w-full"
                style={{ paddingBottom: "56.25%" }}
              >
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src="https://www.youtube.com/embed/7dK1vjxEps0?autoplay=1"
                  title="AIPrice Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>

              {/* Video Description */}
              <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">
                  📋 Nội dung video demo:
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Giới thiệu giao diện AIPrice</li>
                  <li>• Hướng dẫn nhập thông tin bất động sản</li>
                  <li>• Quy trình phân tích AI và xuất báo cáo</li>
                  <li>• Tips sử dụng hiệu quả và tối ưu kết quả</li>
                </ul>
              </div>

              {/* CTA after video */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    closeVideoModal();
                    // Navigate to pricing or AI Price page
                    const userAccessToken =
                      localStorage.getItem("userAccessToken");
                    if (userAccessToken) {
                      navigate("/ai-price");
                    } else {
                      navigate("/login?returnUrl=/ai-price");
                    }
                  }}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-8 py-3 rounded-xl font-bold text-lg hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 shadow-lg"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Thử ngay AIPrice
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingScreen;
